import { useLayoutEffect } from "react";

import { Box, Skeleton } from "@mui/material";

import { syncPreviousSessionHint } from "@/src/auth/previousSessionHint";

import { consumerHeaderIconGroupSx } from "./consumerHeader.styles";
import {
  HEADER_SKELETON_SIZES,
  headerSkeletonAuctionsPillSx,
  headerSkeletonDesktopRowSx,
  headerSkeletonGuestShapeSx,
  headerSkeletonIconSlotSx,
  headerSkeletonIconSx,
  headerSkeletonMobileRowSx,
  headerSkeletonNavLinkSx,
  headerSkeletonPillSx,
  headerSkeletonRootSx,
  headerSkeletonTextSx,
  headerSkeletonUserShapeSx
} from "./consumerHeaderSkeleton.styles";

/** "Kilpailutukset" pill placeholder, also used while the auctions load. */
export const AuctionsPillSkeleton = () => (
  <Skeleton variant="rounded" sx={headerSkeletonAuctionsPillSx} />
);

const IconSkeleton = () => (
  <Box sx={headerSkeletonIconSlotSx}>
    <Skeleton variant="circular" sx={headerSkeletonIconSx} />
  </Box>
);

const ActionPillSkeleton = () => (
  <Skeleton
    variant="rounded"
    sx={headerSkeletonPillSx(
      HEADER_SKELETON_SIZES.actionPillWidth,
      HEADER_SKELETON_SIZES.pillHeight.desktop
    )}
  />
);

interface ConsumerHeaderSkeletonProps {
  /** The wizard header hides "Ostajille" and "Kirjaudu". */
  hideLoggedOutActions: boolean;
}

/**
 * Header actions shown while auth resolves, shaped like the items that will
 * replace them.
 *
 * Both the guest and the signed-in shape are rendered and CSS picks one from
 * the PREVIOUS_SESSION hint that an inline <head> script copies onto <html>
 * before the first paint (see previousSessionHint.ts). The prerendered HTML is
 * the same for everyone, yet a returning user never sees the guest shape, and
 * the markup doesn't depend on localStorage, so hydration matches. Desktop
 * and mobile rows are picked by CSS too, because useMediaQuery doesn't know
 * the viewport while prerendering or hydrating.
 */
const ConsumerHeaderSkeleton = ({
  hideLoggedOutActions
}: ConsumerHeaderSkeletonProps) => {
  // The head script only ran at page load; re-sync before this paints in case
  // the session changed since (e.g. logout, then login, without a reload).
  useLayoutEffect(syncPreviousSessionHint, []);

  return (
    <Box sx={headerSkeletonRootSx} aria-hidden>
      <Box sx={headerSkeletonDesktopRowSx}>
        <Box sx={headerSkeletonGuestShapeSx}>
          {HEADER_SKELETON_SIZES.navLinkWidths.map((width) => (
            <Box key={width} sx={headerSkeletonNavLinkSx(width)}>
              <Skeleton variant="rounded" sx={headerSkeletonTextSx} />
            </Box>
          ))}
          {!hideLoggedOutActions && (
            <>
              <ActionPillSkeleton />
              <ActionPillSkeleton />
            </>
          )}
        </Box>
        <Box sx={headerSkeletonUserShapeSx}>
          <AuctionsPillSkeleton />
          <Box sx={consumerHeaderIconGroupSx}>
            <IconSkeleton />
            <IconSkeleton />
          </Box>
        </Box>
      </Box>
      <Box sx={headerSkeletonMobileRowSx}>
        <Box sx={headerSkeletonGuestShapeSx}>
          {!hideLoggedOutActions && <ActionPillSkeleton />}
        </Box>
        <Box sx={headerSkeletonUserShapeSx}>
          <AuctionsPillSkeleton />
        </Box>
      </Box>
    </Box>
  );
};

export default ConsumerHeaderSkeleton;
